// Copyright (c) 2024 by Audere

import {
  Alert,
  Box,
  Button,
  IconButton,
  ListItem,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import React, { MouseEvent, useCallback, useEffect, useState } from "react";
import { createStyleSheet, globalStyles } from "../utils/styles";
import { Link, useLocation, useNavigate } from "react-router-dom";

import AppLogo from "../../assets/audere-africa-logo.png";
import HomeIcon from "@mui/icons-material/Home";
import LogoutIcon from "@mui/icons-material/Logout";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { googleLogout } from "@react-oauth/google";
import {
  labelApiSlice,
  useLazyGetRolesQuery,
} from "../features/api/labelApiSlice";
import { logOutHelper } from "../features/api/apiSlice";
import {
  PatientGroupRoles,
  selectCurrentToken,
  selectCurrentUserPersistentGroupRole,
  setPatientGroupRoles,
} from "../features/slices/authSlice";
import { useSelector } from "react-redux";

import { AnnotatorListItem } from "./AnnotatorListItem";
import { useAppDispatch } from "../features/store";
import { useCurrentUser } from "../hooks/useCurrentUser";

const PageHeader: React.FC<{
  title: string;
}> = ({ title }) => {
  const isLoggedIn = !!useSelector(selectCurrentToken);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const token = useSelector(selectCurrentToken);
  const group = useSelector(selectCurrentUserPersistentGroupRole).group;
  const user = useCurrentUser();
  const [getRoles, getRolesResult] = useLazyGetRolesQuery({
    pollingInterval: 30000,
  });
  const {
    data: rolesData,
    isFetching: isRolesDataFetching,
    isSuccess: isRolesDataSuccess,
  } = getRolesResult;
  const [hasMultipleRoles, setHasMultipleRoles] = useState<boolean>();

  // Consistently update state for a users authorized roles
  useEffect(() => {
    if (isRolesDataSuccess) {
      const patientGroupRoles: PatientGroupRoles = Object.keys(
        rolesData.groups
      ).reduce((acc: PatientGroupRoles, group: string) => {
        acc[group] = rolesData.groups[group].roles;
        return acc;
      }, {});

      setHasMultipleRoles(
        Object.values(patientGroupRoles)
          .reduce((count, roles) => (
            count + roles.length
          ), 0) > 1
      );
      dispatch(setPatientGroupRoles(patientGroupRoles));
    }
  }, [dispatch, isRolesDataFetching, isRolesDataSuccess, rolesData?.groups]);

  useEffect(() => {
    getRoles(token)
      .unwrap()
      .catch(() => {
        console.error("error getting roles");
      });
  }, [getRoles, token]);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const open = Boolean(anchorEl);
  const id = open ? "account-popover" : undefined;

  const handlePopoverOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const isLabelSelectScreen = location.pathname.endsWith("/label-type-select");

  const handleDashboardsClick = useCallback(() => {
    navigate("/label-type-select");
  }, [navigate]);

  const handleLogoutClick = useCallback(() => {
    googleLogout();
    logOutHelper(dispatch, labelApiSlice.util.resetApiState);
    navigate("/");
  }, [navigate, dispatch]);

  const isInDemoGroup = () => {
    const testGroups = [
      /\b\w+-demo\b/i,
      /\btest-\w*\b/i,
      /\btest\b/i,
      /\b2024-09-SouthAfrica-Au\b/i
    ];
    return testGroups.some((regex) => regex.test(group));
  };

  const popover = () => {
    return (
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Stack spacing={1} padding={1}>
          {!isLabelSelectScreen && (
            <Button
              variant="contained"
              startIcon={<HomeIcon />}
              onClick={handleDashboardsClick}
              aria-label="Dashboards"
              sx={globalStyles.logoThemeButtonSolid}
            >
              Dashboards
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<LogoutIcon />}
            onClick={handleLogoutClick}
            aria-label="Sign out"
            sx={globalStyles.logoThemeButtonOutlined}
          >
            Sign out
          </Button>
        </Stack>
      </Popover>
    );
  };

  return (
    <Box sx={styles.contentContainer}>
      {/* Logo */}
      <Box sx={styles.logoBox}>
        <Link to={"/label-type-select"}>
          <img src={AppLogo} alt="HealthPulse logo" style={{ height: 60 }} />
        </Link>
      </Box>
      {isInDemoGroup() && <Alert sx={{ margin: 5 }} severity="info">Using demo group</Alert>}

      {/* Title */}
      <Box sx={styles.titleBox}>
        <Typography variant="h5" sx={styles.title}>
          {title}
        </Typography>
      </Box>

      {/* User info and menu popover */}
      {isLoggedIn && (
        <Box sx={styles.userInfoBox}>
          <ListItem
            secondaryAction={
              <IconButton
                aria-label="account menu"
                color="inherit"
                onClick={handlePopoverOpen}
              >
                <MoreVertIcon />
              </IconButton>
            }
            disableGutters
          >
            <AnnotatorListItem annotator={user} clickable={hasMultipleRoles} />
          </ListItem>
          {popover()}
        </Box>
      )}
    </Box>
  );
};

const styles = createStyleSheet({
  contentContainer: {
    alignItems: "center",
    borderBottom: "1px solid lightgrey",
    display: "flex",
    paddingX: 2,
    paddingY: 1,
  },
  logoBox: {
    display: "flex",
    alignItems: "center",
    paddingRight: 2,
  },
  titleBox: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  userInfoBox: {
    display: "flex",
    alignItems: "center",
  },
});

export default PageHeader;
